import { createSlice } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import {
	SalesforceCompanySyncTriggerResponse,
	SalesforceListSyncTriggerResponse,
	SalesforceOAuthStatusResponse,
} from "../../pages/Company/schema/salesforce";
import {
	SalesforceConversationSyncResponse,
	SalesforceDealConfigResponse,
	SalesforceDealCreationTriggerResponse,
	SalesforceLeadConfigResponse,
} from "../../pages/Company/schema/salesforceDeal";

import { salesforceApi } from "./salesforce.api";
import { salesforceDealApi } from "./salesforceDeal.api";

interface SalesforceState {
	oauthStatus: SalesforceOAuthStatusResponse | null;
	conversationSync: SalesforceConversationSyncResponse | null;
	companySyncTrigger: SalesforceCompanySyncTriggerResponse | null;
	dealCreationTrigger: SalesforceDealCreationTriggerResponse | null;
	dealConfig: SalesforceDealConfigResponse | null;
	leadConfig: SalesforceLeadConfigResponse | null;
	/**
	 * The per-list sync trigger is the one mirrored query whose **arg** selects the data, so it
	 * gets one bucket per `listId` (web §2). A single "latest" bucket would go stale the moment
	 * the user returns to a list whose entry is still warm: RTK Query serves the cache without
	 * re-dispatching `fulfilled`, so the reducer never runs again.
	 */
	listSyncTriggerByListId: Record<number, SalesforceListSyncTriggerResponse>;
}

const initialState: SalesforceState = {
	oauthStatus: null,
	conversationSync: null,
	companySyncTrigger: null,
	dealCreationTrigger: null,
	dealConfig: null,
	leadConfig: null,
	listSyncTriggerByListId: {},
};

/**
 * One slice fed by **both** Salesforce api files. The split between `salesforce.api.ts` and
 * `salesforceDeal.api.ts` is a file-length concern only — the state they mirror is one
 * integration's settings and belongs in one place.
 */
export const salesforceSlice = createSlice({
	name: "salesforce",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addMatcher(
			salesforceApi.endpoints.getSalesforceOAuthStatus.matchFulfilled,
			(state, { payload }) => {
				state.oauthStatus = payload;
			}
		);
		builder.addMatcher(
			salesforceApi.endpoints.disconnectSalesforceOAuth.matchFulfilled,
			(state) => {
				state.oauthStatus = { connected: false };
			}
		);
		builder.addMatcher(
			salesforceApi.endpoints.getSalesforceCompanySyncTrigger.matchFulfilled,
			(state, { payload }) => {
				state.companySyncTrigger = payload;
			}
		);
		builder.addMatcher(
			salesforceApi.endpoints.updateSalesforceCompanySyncTrigger.matchFulfilled,
			(state, { payload }) => {
				state.companySyncTrigger = payload;
			}
		);
		builder.addMatcher(
			salesforceDealApi.endpoints.getSalesforceConversationSync.matchFulfilled,
			(state, { payload }) => {
				state.conversationSync = payload;
			}
		);
		builder.addMatcher(
			salesforceDealApi.endpoints.updateSalesforceConversationSync.matchFulfilled,
			(state, { payload }) => {
				state.conversationSync = payload;
			}
		);
		builder.addMatcher(
			salesforceDealApi.endpoints.getSalesforceDealCreationTrigger.matchFulfilled,
			(state, { payload }) => {
				state.dealCreationTrigger = payload;
			}
		);
		builder.addMatcher(
			salesforceDealApi.endpoints.updateSalesforceDealCreationTrigger.matchFulfilled,
			(state, { payload }) => {
				state.dealCreationTrigger = payload;
			}
		);
		builder.addMatcher(
			salesforceDealApi.endpoints.getSalesforceDealConfig.matchFulfilled,
			(state, { payload }) => {
				state.dealConfig = payload;
			}
		);
		builder.addMatcher(
			salesforceDealApi.endpoints.saveSalesforceDealConfig.matchFulfilled,
			(state, { payload }) => {
				state.dealConfig = payload;
			}
		);
		builder.addMatcher(
			salesforceDealApi.endpoints.getSalesforceLeadConfig.matchFulfilled,
			(state, { payload }) => {
				state.leadConfig = payload;
			}
		);
		builder.addMatcher(
			salesforceDealApi.endpoints.saveSalesforceLeadConfig.matchFulfilled,
			(state, { payload }) => {
				state.leadConfig = payload;
			}
		);
		// Filed under the listId the response was **requested** for, never the currently
		// selected one — a response can land after the user has already switched lists.
		builder.addMatcher(
			salesforceApi.endpoints.getSalesforceListSyncTrigger.matchFulfilled,
			(state, { payload, meta }) => {
				state.listSyncTriggerByListId[meta.arg.originalArgs] = payload;
			}
		);
		builder.addMatcher(
			salesforceApi.endpoints.updateSalesforceListSyncTrigger.matchFulfilled,
			(state, { payload, meta }) => {
				state.listSyncTriggerByListId[meta.arg.originalArgs.listId] = payload;
			}
		);
	},
});

export const salesforceReducer = salesforceSlice.reducer;

export const salesforceSelector = (state: RootState) => state[salesforceSlice.name];

export const salesforceListSyncTriggerSelector =
	(listId: number) =>
	(state: RootState): SalesforceListSyncTriggerResponse | undefined =>
		state[salesforceSlice.name].listSyncTriggerByListId[listId];
